import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Stack,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import BlockIcon from '@mui/icons-material/Block';
import { useTranslation } from 'react-i18next';

export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { t } = useTranslation();

  let status = 500;
  let title = t('errors.generic');
  let message = t('errors.serverError');
  let Icon = ErrorOutlineIcon;

  if (isRouteErrorResponse(error)) {
    status = error.status;

    switch (status) {
      case 404:
        title = t('errors.notFound');
        message = t('errors.notFoundMessage');
        Icon = SearchOffIcon;
        break;
      case 401:
        title = t('errors.unauthorized');
        message = t('errors.unauthorizedMessage');
        Icon = BlockIcon;
        break;
      case 403:
        title = t('errors.forbidden');
        message = t('errors.forbiddenMessage');
        Icon = BlockIcon;
        break;
      default:
        title = t('errors.generic');
        message = error.statusText || t('errors.serverError');
    }
  }

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            width: '100%',
          }}
        >
          <Icon
            sx={{
              fontSize: 64,
              color: status === 404 ? 'warning.main' : 'error.main',
              mb: 2,
            }}
          />

          {status !== 500 && (
            <Typography
              variant="h2"
              sx={{
                fontWeight: 'bold',
                color: 'text.secondary',
                mb: 1,
              }}
            >
              {status}
            </Typography>
          )}

          <Typography variant="h5" gutterBottom fontWeight="medium">
            {title}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            {message}
          </Typography>

          {import.meta.env.DEV && error instanceof Error && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: 'action.hover',
                textAlign: 'left',
                overflow: 'auto',
                maxHeight: 200,
              }}
            >
              <Typography
                variant="caption"
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  m: 0,
                }}
              >
                {error.message}
                {'\n'}
                {error.stack}
              </Typography>
            </Paper>
          )}

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
          >
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleGoBack}
            >
              {t('back')}
            </Button>
            {status === 500 && (
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
              >
                {t('errors.refresh')}
              </Button>
            )}
            <Button
              variant={status === 500 ? 'outlined' : 'contained'}
              startIcon={<HomeIcon />}
              onClick={handleGoHome}
            >
              {t('errors.goHome')}
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Container>
  );
}
